import { useEffect, useCallback, useSyncExternalStore } from 'react';

type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'foodweb_theme';

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveIsDark(theme: Theme): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return getSystemPrefersDark();
}

function applyTheme(isDark: boolean) {
  const root = document.documentElement;
  if (isDark) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', isDark ? '#1f2937' : '#f9fafb');
  }
}

// Module-level shared store so all useTheme() instances share state
let currentTheme: Theme = typeof window !== 'undefined'
  ? (localStorage.getItem(STORAGE_KEY) as Theme) || 'system'
  : 'system';

const listeners = new Set<() => void>();

function getSnapshot(): Theme {
  return currentTheme;
}

function getServerSnapshot(): Theme {
  return 'system';
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function setThemeValue(next: Theme) {
  const update = () => {
    currentTheme = next;
    const isDark = resolveIsDark(next);
    applyTheme(isDark);
    localStorage.setItem(STORAGE_KEY, next);
    listeners.forEach(fn => fn());
  };

  // Use View Transitions API for a smooth GPU-composited crossfade
  if (typeof document !== 'undefined' && 'startViewTransition' in document) {
    (document as any).startViewTransition(update);
  } else {
    update();
  }
}

// Listen for system preference changes
if (typeof window !== 'undefined') {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (currentTheme === 'system') {
      applyTheme(mq.matches);
      listeners.forEach(fn => fn());
    }
  });
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const isDark = resolveIsDark(theme);

  // Apply on mount (for initial hydration)
  useEffect(() => {
    applyTheme(resolveIsDark(currentTheme));
  }, []);

  const toggleTheme = useCallback(() => {
    const wasDark = resolveIsDark(currentTheme);
    setThemeValue(wasDark ? 'light' : 'dark');
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeValue(next);
  }, []);

  return { theme, setTheme, toggleTheme, isDark };
}
